/**
 * Format a patient display name as "First Last Suffix".
 * Empty parts are omitted. Does not include middle name or prefix.
 *
 * @param {{ first_name?: string|null, last_name?: string|null, suffix?: string|null }|null|undefined} person
 * @param {{ order?: 'firstLast'|'lastFirst' }} [options]
 * @returns {string}
 */
export function formatPatientName(person, options = {}) {
    if (!person || typeof person !== 'object') {
        return ''
    }

    const first = String(person.first_name ?? '').trim()
    const last = String(person.last_name ?? '').trim()
    const suffix = String(person.suffix ?? '').trim()
    const order = options.order === 'lastFirst' ? 'lastFirst' : 'firstLast'

    const parts =
        order === 'lastFirst'
            ? [last, first, suffix]
            : [first, last, suffix]

    return parts.filter(Boolean).join(' ')
}

/**
 * True when insuredName matches the patient with or without suffix.
 * Supports older saved values that were stored as "First Last" only.
 *
 * @param {string|null|undefined} insuredName
 * @param {{ first_name?: string|null, last_name?: string|null, suffix?: string|null }|null|undefined} patient
 * @returns {boolean}
 */
export function isPatientInsuredName(insuredName, patient) {
    if (!insuredName || !patient) {
        return false
    }

    const normalized = String(insuredName).trim().toLowerCase()
    if (!normalized) {
        return false
    }

    const withSuffix = formatPatientName(patient).toLowerCase()
    const withoutSuffix = formatPatientName({
        first_name: patient.first_name,
        last_name: patient.last_name,
    }).toLowerCase()

    return normalized === withSuffix || normalized === withoutSuffix
}

/**
 * True when two name values match after trim and case-insensitive compare.
 * Empty / null / undefined all count as empty.
 *
 * @param {string|null|undefined} a
 * @param {string|null|undefined} b
 * @returns {boolean}
 */
export function namesMatchIgnoreCase(a, b) {
    const normalize = (value) => String(value ?? '').trim().toLowerCase()
    return normalize(a) === normalize(b)
}
